import { getAllAnswersExtended, getQuizTitle } from "@/classes/QuizInternals";
import { getQuizParams } from "@/utils/quiz";
import { communicationStyles, initialContext, nutritionAreas, paymentMethods, usaStates } from "@/utils/constants";
import { getRDEIds } from "@/apps/appointment-scheduler/utils";
import { getStage } from "@/utils/functions";
import { Api } from "@/api";
import { getLocalStorage, setLocalStorage } from "@/utils/localStorage";
import type { CustomDietitianProfile, PaymentType, QuizAnswers, QuizInstanceData, QuizTitle } from "shared/types";
import { defaultProvidersProd, defaultProvidersStaging, insuranceList } from "shared/constants";
import { objectValueMatch } from "shared/classes/utils";
import type { AppParams } from "@/types/scheduling";
import { handleGetURLParams } from "@/state/appointment/setup/action-handlers";
import { Track } from "@/classes/Track";

interface CustomizationData {
  token: string;
  RDEIDs: string[];
  requestParams: {
    extendedAnswers: any[];
  };
  response: Record<string, CustomDietitianProfile>;
  error?: string;
}

function getCustomizationLS(): Partial<CustomizationData> {
  return getLocalStorage("dietitianCustomization") || {};
}

function setCustomizationLS(data: Partial<CustomizationData>, append: boolean = true) {
  const existingData = append ? getCustomizationLS() : {};
  const newData = { ...existingData, ...data };
  setLocalStorage("dietitianCustomization", newData);
}

/**
 * implementation of the handleGetUrlParamsForCustomization function but without the context
 */
function handleGetUrlParamsForCustomization(params: AppParams) {
  // deepcopy
  const { result } = JSON.parse(JSON.stringify(initialContext));

  if (params.expertise) {
    result.nutrition_area.value =
      objectValueMatch(params.expertise, nutritionAreas, "value", "contains") || "General Health";
    result.nutrition_area.label = objectValueMatch(params.expertise, nutritionAreas) || "Select nutrition area";
    result.nutrition_area.key = "nutrition_area";
  }
  if (params.nutrition_area) {
    result.nutrition_area.value =
      objectValueMatch(params.nutrition_area, nutritionAreas, "value") || "General Health";
    result.nutrition_area.label =
      objectValueMatch(params.nutrition_area, nutritionAreas) || "Select nutrition area";
    result.nutrition_area.key = "nutrition_area";
  }
  if (params.state) {
    result.state.value = objectValueMatch(params.state, usaStates, "value") || "error";
    result.state.label = objectValueMatch(params.state, usaStates) || "Select your state";
    result.state.key = "state";
  }
  if (params.communication_style) {
    result.communication_style.value =
      objectValueMatch(params.communication_style, communicationStyles, "value") || "error";
    result.communication_style.label =
      objectValueMatch(params.communication_style, communicationStyles) || "Select communication style";
    result.communication_style.key = "communication_style";
  }
  if (params.payment_method) {
    result.payment_method.value = objectValueMatch(params.payment_method, paymentMethods, "value") || "error";
    result.payment_method.label =
      objectValueMatch(params.payment_method, paymentMethods) || "Select your payment method";
    result.payment_method.key = "payment_method";
  }
  if (params.insurance_provider) {
    result.insurance_provider = {
      value: objectValueMatch(params.insurance_provider, insuranceList, "value") || "error",
      label: objectValueMatch(params.insurance_provider, insuranceList) || "Select your insurance",
      key: "insurance_provider",
    };
  }
  if (params.insurance_provider_other) {
    result.insurance_provider_other = params.insurance_provider_other;
  }
  if (params.lead_name) {
    result.lead_name = params.lead_name;
  }
  if (params.lead_email) {
    result.lead_email = params.lead_email;
  }
  if (params.lead_phone_number) {
    result.lead_phone_number = params.lead_phone_number;
  }
  if (params.diabetes_insulin_pump) {
    result.diabetes_insulin_pump = params.diabetes_insulin_pump;
  }

  return result;
}

export async function sendCustomizationRequest(quizData: any, resend: boolean = false) {
  if (!resend) {
    const customizationData = getCustomizationLS();
    if (customizationData.token) {
      console.debug("Customization request already sent, skipping new request");
      return;
    }
  }

  // pulling questions along with the answers
  const answers = getAllAnswersExtended(quizData);

  // we can launch this if we have state and insurance provider at this moment
  if (
    !answers.some((a) => a.questionIdentifier === "state") ||
    !answers.some((a) => a.questionIdentifier === "insurance_provider")
  ) {
    console.debug("There are no state or insurance provider answers, skipping AI dietitian match query");
    return;
  }

  // THE BIG FLOW
  // 1. quiz saves the data via (1.1) Track.storeQuizData and (1.2) Track.storeQuizCompletionData
  //  then app is started, prepares (2) some params and launches the state machine
  //  the state machine performs (3.1) handleGetOverwriteOptions and (3.2) handleGetURLParams (enqueued in setupApp)
  //   pulling the data from the quizInstance LS and tweaking it a bit
  //  then this data is fed into getSlots (here into getRDEIds)

  // (1.1) Track.storeQuizData();
  let quizDataTracked: Partial<QuizInstanceData> = {
    quizUid: quizData.quiz.uniqueIdentifier,
    title: quizData.quiz.title,
    quizId: quizData.quiz.id,
    quizInstanceId: quizData.uid,
    quizVersion: quizData.quiz.version,
  };
  // (1.2) Track.storeQuizCompletionData(); TODO - maybe deduplicate this with Track.ts
  const track = new Track();
  quizDataTracked = {
    ...quizDataTracked,
    quizLoadedAt: getLocalStorage("quizSeenAt") || localStorage.getItem("quizSeenAt"),
    answers: track.unifyNutritionArea(track.extractContactData(quizData.answers)),
    insuranceActiveAndEligible: false,
    isLowIntent: false,
    metadata: quizData.quiz.metadata,
    answersFull: track.extractContactDataFull(track.prepareQuestions(quizData)),
    quizCompletedAt: new Date().toISOString(),
    resultMetadata: quizData.result && quizData.result[0] ? quizData.result[0].metadata : {},
    externalUrl: "",
  };
  // now this data is assumed to be stored in localStorage as "quizInstance"
  // then the appointment scheduler app is started

  // (2) the following is in app/main.ts
  const params: Partial<AppParams> = {};
  params.flow = {
    name: "quiz_flow",
    id: getQuizTitle(quizData) as QuizTitle,
  };

  // 3.1 skipping handleGetOverwriteOptions because it is about the app direct input
  // (3.2) initiating the assumed app context as in handleGetURLParams
  const paramsFromQuiz = getQuizParams(quizDataTracked.answers); // called from getParams in handleGetURLParams
  const result = handleGetUrlParamsForCustomization(paramsFromQuiz);

  // now finally preparing the final params for getRDEIds call as in appointment.machine.ts getSlots -> invoke
  const appParams: AppParams = {
    nutrition_area: result.nutrition_area.value,
    state: result.state.value,
    provider_id: undefined,
    provider_ids: [],
    plan_id: undefined,
    flow: params.flow,
    insurance_provider: result.insurance_provider?.value,
    payment_method: result.payment_method.value as PaymentType,
    diabetes_insulin_pump: result.diabetes_insulin_pump,
    using_rematch_tool: false,
    op_mode: undefined,
  };


  const RDEIDs = await getRDEIds(appParams);

  // looking for RDEs in artificial slots
  if (RDEIDs.length === 0) {
    const isDev = process.env.NODE_ENV === "development";
    const stage = getStage(isDev);
    const baseRDEsToAdd = stage === "local" || stage === "staging" ? defaultProvidersStaging : defaultProvidersProd;
    RDEIDs.push(...baseRDEsToAdd);
  }

  const customizationRequestResponse = await new Api().getCustomDietitianProfiles(RDEIDs, { extendedAnswers: answers });

  setCustomizationLS({
    ...customizationRequestResponse,
    RDEIDs,
    requestParams: { extendedAnswers: answers },
  });
  console.debug(`Customization request sent, token: ${customizationRequestResponse.token}`);
}

export async function retrieveCustomizationResult() {
  const customizationData = getCustomizationLS();

  if (customizationData.response) return customizationData.response;
  if (customizationData.error) return false;

  if (!customizationData.token) {
    console.debug("No customization token found, skipping retrieval");
    return;
  }

  const response = await new Api().getCustomDietitianProfilesResult(customizationData.token);
  if (response && response.response) {
    setCustomizationLS({ response: response.response });
    console.debug("Customization result retrieved successfully");
    return response.response;
  } else if (response && response.status === "error") {
    console.error(`Error retrieving customization result: ${response.message}`);
    setCustomizationLS({ error: response.message });
    return false;
  } else if (response && response.status === "processing") {
    console.warn("Customization result is still processing");
    return false;
  }
}

export function getCustomizationResponse(RDEId: string | number) {
  const customizationData = getCustomizationLS();
  if (customizationData.response && customizationData.response[RDEId] && customizationData.response[RDEId].customCV.length > 40) {
    return customizationData.response[RDEId];
  }

  return null;
}